import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { PRODUCTS } from "@/data/products";

const Products = () => (
    <>
        <Seo
            title="Our Products | Coaching Crafters AI"
            description="AI Web Based Software, ERP Solution, Interactive Panels and Solar Systems — four product lines from Coaching Crafters AI, each engineered and supported end to end."
            path="/products"
        />

        <section className="relative overflow-hidden" data-testid="products-hero">
            <div
                className="pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem] rounded-full bg-ember/10 blur-3xl"
                aria-hidden="true"
            />
            <div className="mx-auto max-w-7xl px-6 py-20 text-center sm:px-10 lg:py-24">
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-ember">Our Products</p>
                <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
                    <MaskedLine delay={0.1}>Four product lines.</MaskedLine>
                    <MaskedLine delay={0.25}>
                        One obsession with <em className="font-medium italic text-ember">craft.</em>
                    </MaskedLine>
                </h1>
                <Reveal delay={0.35}>
                    <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-ink/60 sm:text-lg">
                        Software that thinks, systems that organise, panels that respond and rooftops that pay for
                        themselves — every product designed, delivered and supported by one accountable team.
                    </p>
                </Reveal>
            </div>
        </section>

        <section className="mx-auto max-w-7xl space-y-16 px-6 pb-24 sm:px-10" data-testid="products-list">
            {PRODUCTS.map((p, i) => (
                <Reveal key={p.slug}>
                    <div
                        data-testid={`product-card-${p.slug}`}
                        className={`grid grid-cols-1 items-center gap-10 lg:grid-cols-2 ${
                            i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                        }`}
                    >
                        <motion.div whileHover={{ scale: 1.015 }} transition={{ duration: 0.3 }} className="relative">
                            <div className="absolute -inset-5 rounded-[2.5rem] bg-ember/10 blur-2xl" aria-hidden="true" />
                            <div className="relative overflow-hidden rounded-3xl border border-white/40 shadow-[0_30px_60px_-24px_rgba(26,25,24,0.35)]">
                                <img
                                    src={p.image}
                                    alt={p.imageAlt}
                                    className="aspect-[3/2] w-full object-cover"
                                    loading={i === 0 ? "eager" : "lazy"}
                                />
                            </div>
                        </motion.div>
                        <div>
                            <div className="flex items-center gap-4">
                                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ember/10">
                                    <p.icon className="h-6 w-6 text-ember" />
                                </span>
                                <span className="font-serif text-4xl font-semibold text-ember/25">
                                    0{i + 1}
                                </span>
                            </div>
                            <h2 className="mt-5 font-serif text-3xl font-semibold tracking-tight text-ink">
                                {p.name}
                            </h2>
                            <p className="mt-2 font-serif text-lg italic text-ember">{p.tagline}</p>
                            <p className="mt-4 text-base leading-relaxed text-ink/60">{p.overview}</p>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {p.glance.slice(0, 3).map((g) => (
                                    <span
                                        key={g.label}
                                        className="rounded-full border border-[#E2E8F4] bg-white px-4 py-1.5 text-xs font-medium text-ink/70"
                                    >
                                        <span className="font-semibold text-ember">{g.value}</span> {g.label}
                                    </span>
                                ))}
                            </div>
                            <div className="mt-8 flex flex-wrap gap-4">
                                <Link
                                    to={`/products/${p.slug}`}
                                    data-testid={`product-${p.slug}-learn-more`}
                                    className="group inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-semibold text-bone transition-colors hover:bg-ember-dark"
                                >
                                    Learn more
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </Link>
                                <Link
                                    to="/contact"
                                    state={{ product: p.name }}
                                    data-testid={`product-${p.slug}-enquire`}
                                    className="rounded-full border border-ink/15 px-6 py-3 text-sm font-semibold text-ink transition-colors hover:border-ember hover:text-ember"
                                >
                                    Enquire
                                </Link>
                            </div>
                        </div>
                    </div>
                </Reveal>
            ))}
        </section>
    </>
);

export default Products;
